import { PredefinedFileTags } from '@lite-workspace/dto-fotoclass-photo';
import {
  BoxType,
  smartObjectConvertsToImage,
  type CustomImageTemplateBox,
  type CustomObjectTemplateBox,
  type CustomTemplateBox,
  type CustomTemplateBoxBase,
  type CustomVectorTemplateBox,
} from './custom-template';

/** Значения совпадают с `BoxType.IMAGE` / `OBJECT` / `BACKGROUND`: член enum подходит сюда без приведения. */
export type BoxConvertTarget = 'image' | 'object' | 'background';

const BOX_CONVERT_TARGETS: readonly BoxConvertTarget[] = ['image', 'object', 'background'];

export function isBoxConvertTarget(value: unknown): value is BoxConvertTarget {
  return (BOX_CONVERT_TARGETS as readonly unknown[]).includes(value);
}

// Строки типа совпадают со значениями `DrawableData.type`: проба обслуживает и бокс шаблона, и drawable альбома.
type BoxConvertProbe = { type?: string; params?: object };

// Вектор и растровый smart object фоном вышли бы пустыми; рамка, закреплённый слой и «рамка в группе» сужают список у себя.
export function boxConvertTargets(box: BoxConvertProbe): readonly BoxConvertTarget[] {
  switch (box.type) {
    case BoxType.IMAGE:
    case BoxType.OBJECT:
    case BoxType.BACKGROUND:
      return BOX_CONVERT_TARGETS.filter(t => t !== box.type);
    case BoxType.VECTOR:
      return ['image'];
    case BoxType.SMART_OBJECT:
      return smartObjectConvertsToImage(box) ? ['image'] : [];
    default:
      return [];
  }
}

// Живой декор и фон без url движок не строит вовсе (`validate_live_input`), роняя весь разворот; пустая рамка — может.
export function convertKeepsLiveStyle(to: BoxConvertTarget, sourceUrl: string | undefined): boolean {
  return to === 'image' || Boolean(sourceUrl);
}

/** Blending Options слоя и якорь его живых эффектов — общая форма бокса шаблона и drawable альбома. */
export type LayerOptionsFields = Pick<
  CustomTemplateBoxBase,
  | 'blendIf'
  | 'knockout'
  | 'knockoutBackdrop'
  | 'blendClipped'
  | 'transparencyShapes'
  | 'effectReferencePoint'
  | 'effectDocumentOrigin'
>;

/**
 * Что смена типа несёт из Blending Options слоя. Blend If, knockout, blend clipped и transparency shapes движок
 * рисует и у нелайвового слоя, поэтому они едут всегда, как opacity и blendMode. Якорь живых эффектов движок
 * читает только у живого слоя: без `keepLive` он остался бы сиротой. Смена типа слой не двигает — якорь едет как
 * есть. Группа сюда не попадает: её поля лежат в `params.psdGroup`.
 */
export function carriedLayerOptions(source: LayerOptionsFields, keepLive: boolean): LayerOptionsFields {
  return {
    ...(source.blendIf && { blendIf: source.blendIf }),
    ...(source.knockout && { knockout: source.knockout }),
    ...(source.knockout && source.knockoutBackdrop && { knockoutBackdrop: source.knockoutBackdrop }),
    ...(source.blendClipped === false && { blendClipped: false as const }),
    ...(source.transparencyShapes === false && { transparencyShapes: false as const }),
    ...(keepLive && source.effectReferencePoint && { effectReferencePoint: { ...source.effectReferencePoint } }),
    ...(keepLive && source.effectDocumentOrigin && { effectDocumentOrigin: { ...source.effectDocumentOrigin } }),
  };
}

// В распарсенном PSD фоторамка приходит боксом `object`: роль рамки живёт только в параметрах
// `image`, и редактор даёт её тем же путём — сначала превращает бокс в рамку (design-edit.ops.ts,
// convertBoxType → toImageBox), потом пишет роль. Порт ветки object → image: кроп снимается (рамка
// САМА себе окно), припрятанный imageFrameIntent разворачивается обратно, маска переезжает.
// Это порт ветки объекта, не всего редакторского пути. Намеренно: PSD-происхождение бокса (psdLayerId,
// groupId, name) остаётся на месте — иначе разметка ролей рвала бы связь листа с его PSD-группой.
// Не перенесено: редактор пересобирает эффекты из слоя (живые или снимок макета плюс rawEffects) —
// здесь effects едут как есть; не несёт и decorIntent, сужение до видимой части и позу слоя.
const objectToImageBox = (box: CustomObjectTemplateBox): CustomImageTemplateBox => {
  const { imageFrameIntent: intent, maskDataUrl, maskUrl, maskFileId, maskBaseBoxId } = box.params;
  const { contentRect: _crop, ...base } = box;
  // Обтравка слоем-основой переживает смену рода, как в редакторе (imageBoxOf): иначе фото вылезает из основы.
  const clip = maskBaseBoxId ? { maskBaseBoxId } : {};
  return {
    ...base,
    type: BoxType.IMAGE,
    params: intent
      ? {
          sourceRef: intent.sourceRef,
          versions: intent.versions,
          previewPixelData: intent.previewPixelData,
          previewThumbHash: intent.previewThumbHash,
          paths: intent.paths,
          maskDataUrl,
          maskUrl,
          maskFileId,
          frameUrl: intent.frameUrl,
          frameFileId: intent.frameFileId,
          frameWindow: intent.frameWindow,
          ...clip,
        }
      : { fileTag: PredefinedFileTags.PORTRAIT, maskDataUrl, maskUrl, maskFileId, ...clip },
    ...(intent?.orientation ? { orientation: intent.orientation } : {}),
  };
};

// Фотослот распарсенного PSD чаще всего вектор, и редактор превращает его в рамку тем же одним движением:
// силуэт вектора становится окном рамки, всё остальное из параметров вектора (заливка, обводка, растр) уходит —
// рамке его нести некуда (design-edit.ops.ts, toImageBox). Кроп снимается, как и в ветке object.
const vectorToImageBox = (box: CustomVectorTemplateBox): CustomImageTemplateBox => {
  const { contentRect: _crop, ...base } = box;
  const { paths, maskBaseBoxId } = box.params;
  return { ...base, type: BoxType.IMAGE, params: { paths, ...(maskBaseBoxId ? { maskBaseBoxId } : {}) } };
};

/** Бокс как фоторамка — один порт редакторского «Тип элемента → Рамка изображения» для парсера и editor-MCP. */
export const asImageFrame = (box: CustomTemplateBox): CustomImageTemplateBox => {
  if (box.type === BoxType.IMAGE) return box;
  if (box.type === BoxType.VECTOR) return vectorToImageBox(box);
  return objectToImageBox(box as CustomObjectTemplateBox);
};
